import { useMemo, useState } from 'react'
import { Outlet, useLocation, useNavigate, Link } from 'react-router-dom'
import {
  Bell,
  Building2,
  Car,
  Clock,
  CreditCard,
  LayoutDashboard,
  LogOut,
  Menu,
  Moon,
  PackageOpen,
  Plus,
  Settings,
  ShoppingCart,
  Store,
  Sun,
  Users,
  Wrench,
  MessageSquare,
  ShoppingBag,
  ExternalLink,
} from 'lucide-react'

import { PageContainer } from '@/components/common'
import { Brand, Sidebar, type SidebarItem } from '@/components/layout'
import {
  Avatar,
  Breadcrumb,
  Button,
  Drawer,
  DrawerContent,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui'
import { appRoutes } from '@/constants'
import { useAuth } from '@/providers/auth-provider'
import { useTheme } from '@/providers/theme-provider'
import { useLogout } from '@/features/auth/hooks'
import { useCart } from '@/modules/cart/hooks/use-cart'
import { useMyOrders } from '@/modules/cart/hooks/use-cart'
import { CartDrawer } from '@/modules/cart/components/cart-drawer'

function useDashboardBreadcrumbs() {
  const { pathname } = useLocation()

  return useMemo(() => {
    const segments = pathname.split('/').filter(Boolean).slice(1)
    const items: { label: string; to: string }[] = [{ label: 'Dashboard', to: appRoutes.dashboard.root }]
    let path: string = appRoutes.dashboard.root
    for (const segment of segments) {
      path = `${path}/${segment}`
      items.push({
        label: segment.charAt(0).toUpperCase() + segment.slice(1).replace('-', ' '),
        to: path,
      })
    }
    return items
  }, [pathname])
}

export function DashboardLayout() {
  const [mobileOpen, setMobileOpen] = useState(false)
  const [isCartOpen, setIsCartOpen] = useState(false)
  const navigate = useNavigate()
  const breadcrumbItems = useDashboardBreadcrumbs()
  const { user, redirectPath, dashboard, role, me } = useAuth()
  const { theme, toggleTheme } = useTheme()
  const logout = useLogout()

  const { data: cartData } = useCart()
  const { data: myOrders = [] } = useMyOrders()
  const cartItemCount = cartData?.items?.reduce((acc: number, item: any) => acc + item.quantity, 0) || 0

  const dashboardHref =
    role === 'ADMIN'
      ? '/admin'
      : role === 'SELLER'
      ? '/seller'
      : role === 'DEALER'
      ? '/dealer'
      : '/dashboard'

  const sidebarItems = useMemo<SidebarItem[]>(() => {
    const isApprovedMerchant = role === 'SELLER' || role === 'DEALER'

    if (role === 'CUSTOMER') {
      const items: SidebarItem[] = [
        {
          label: 'Dashboard Overview',
          to: appRoutes.dashboard.root,
          icon: <LayoutDashboard />,
          end: true,
          category: 'Personal Portal',
        },
        {
          label: 'My Parts Orders',
          to: appRoutes.customer.orders,
          icon: <PackageOpen />,
          category: 'Personal Portal',
          badge: myOrders.length > 0 ? (
            <span className="flex size-5 items-center justify-center rounded-full bg-[#CF2F2F] text-[10px] font-extrabold text-white">
              {myOrders.length}
            </span>
          ) : null,
        },
        {
          label: 'Live Inquiries & Chat',
          to: appRoutes.customer.messages,
          icon: <MessageSquare />,
          category: 'Personal Portal',
        },
        {
          label: 'Explore Supercars',
          to: appRoutes.cars,
          icon: <Car />,
          category: 'Marketplace',
        },
        {
          label: 'Spare Parts Catalog',
          to: appRoutes.parts,
          icon: <Wrench />,
          category: 'Marketplace',
        },
      ]

      if (!isApprovedMerchant) {
        items.push(
          {
            label: 'Become a Seller',
            to: appRoutes.customer.becomeSeller,
            icon: <Store />,
            category: 'Business Onboarding',
          },
          {
            label: 'Become a Dealer',
            to: appRoutes.customer.becomeDealer,
            icon: <Building2 />,
            category: 'Business Onboarding',
          },
        )
      }

      items.push({
        label: 'Verification History',
        to: appRoutes.customer.approvalHistory,
        icon: <Clock />,
        category: 'Business Onboarding',
      })

      return items
    }

    if (role === 'SELLER') {
      return [
        { label: 'Dashboard', to: '/seller', icon: <LayoutDashboard />, end: true, category: 'Seller Console' },
        { label: 'Customer Inquiries', to: '/seller/messages', icon: <MessageSquare />, category: 'Seller Console' },
        {
          label: 'My Parts Orders',
          to: '/seller/orders',
          icon: <PackageOpen />,
          category: 'Seller Console',
          badge: myOrders.length > 0 ? (
            <span className="flex size-5 items-center justify-center rounded-full bg-[#CF2F2F] text-[10px] font-extrabold text-white">
              {myOrders.length}
            </span>
          ) : null,
        },
        { label: 'My Listed Cars', to: '/seller/cars', icon: <Car />, end: true, category: 'Vehicles' },
        { label: '+ Add Car Listing', to: '/seller/cars/new', icon: <Plus />, category: 'Vehicles' },
        { label: 'Billing & Plans', to: '/seller/billing', icon: <CreditCard />, category: 'Finance' },
      ]
    }

    if (role === 'DEALER') {
      return [
        { label: 'Dashboard', to: '/dealer', icon: <LayoutDashboard />, end: true, category: 'Dealership Hub' },
        { label: 'Customer Inquiries', to: '/dealer/messages', icon: <MessageSquare />, category: 'Dealership Hub' },
        {
          label: 'My Parts Orders',
          to: '/dealer/orders',
          icon: <PackageOpen />,
          category: 'Dealership Hub',
          badge: myOrders.length > 0 ? (
            <span className="flex size-5 items-center justify-center rounded-full bg-[#CF2F2F] text-[10px] font-extrabold text-white">
              {myOrders.length}
            </span>
          ) : null,
        },
        { label: 'Vehicles Fleet', to: '/dealer/cars', icon: <Building2 />, end: true, category: 'Fleet Management' },
        { label: '+ Add Vehicle', to: '/dealer/cars/new', icon: <Plus />, category: 'Fleet Management' },
        { label: 'Billing & Plans', to: '/dealer/billing', icon: <CreditCard />, category: 'Finance' },
      ]
    }

    return [
      { label: 'Dashboard', to: appRoutes.dashboard.root, icon: <LayoutDashboard />, end: true },
      { label: 'Messages', to: '/dashboard/messages', icon: <MessageSquare /> },
      { label: 'Cars', to: '/seller/cars', icon: <Car /> },
      { label: 'Billing', to: '/seller/billing', icon: <CreditCard /> },
    ]
  }, [role, myOrders.length])

  const initials = `${user?.firstName?.[0] ?? 'U'}${user?.lastName?.[0] ?? ''}`.toUpperCase()

  const handleLogout = async () => {
    await logout.mutateAsync()
    navigate(appRoutes.auth.login, { replace: true })
  }

  const sidebarFooter = (
    <div className="space-y-3">
      {/* Return to Storefront link */}
      <Link
        to="/"
        className="flex items-center justify-between px-3 py-2 rounded-xl bg-accent/30 hover:bg-[#CF2F2F]/10 hover:text-[#CF2F2F] text-xs font-bold text-foreground/80 transition-colors border border-border/40"
      >
        <span className="flex items-center gap-2">
          <Store className="size-4 text-[#CF2F2F]" />
          <span>Back to Storefront</span>
        </span>
        <ExternalLink className="size-3 text-muted-foreground" />
      </Link>

      {/* User profile preview chip */}
      <div className="flex items-center justify-between p-2 rounded-xl border border-border/60 bg-muted/20">
        <div className="flex items-center gap-2.5 min-w-0">
          <div className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-[#CF2F2F]/10 font-bold text-xs text-[#CF2F2F]">
            {initials}
          </div>
          <div className="min-w-0">
            <p className="truncate text-xs font-bold text-foreground">
              {user?.firstName ? `${user.firstName} ${user.lastName || ''}` : 'Customer Account'}
            </p>
            <p className="truncate text-[10px] text-muted-foreground">{user?.email || 'Logged in'}</p>
          </div>
        </div>
        <button
          type="button"
          onClick={handleLogout}
          className="p-1.5 text-muted-foreground hover:text-rose-500 rounded-lg hover:bg-rose-500/10 transition-colors"
          title="Sign Out"
        >
          <LogOut className="size-4" />
        </button>
      </div>
    </div>
  )

  const sidebar = (
    <Sidebar
      brand={
        <div className="space-y-2">
          <Brand />
          <div className="text-[10px] font-extrabold uppercase tracking-widest text-[#CF2F2F] px-1">
            Client Control Center
          </div>
        </div>
      }
      items={sidebarItems}
      footer={sidebarFooter}
    />
  )

  return (
    <div className="min-h-screen bg-background">
      <aside className="fixed inset-y-0 left-0 z-30 hidden w-72 border-r border-border/60 bg-background/80 backdrop-blur-xl lg:block">
        {sidebar}
      </aside>

      <Drawer open={mobileOpen} onOpenChange={setMobileOpen} placement="left">
        <DrawerContent hideCloseButton className="w-72 p-0 bg-background">
          {sidebar}
        </DrawerContent>
      </Drawer>

      <div className="lg:pl-72">
        <header className="sticky top-0 z-20 flex h-16 items-center gap-3 border-b border-border/60 bg-background/70 px-4 backdrop-blur-xl sm:px-6 lg:px-8">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="lg:hidden"
            onClick={() => setMobileOpen(true)}
            aria-label="Open navigation"
          >
            <Menu aria-hidden className="size-5" />
          </Button>

          <Breadcrumb items={breadcrumbItems} className="hidden sm:flex" />

          <div className="ml-auto flex items-center gap-2 sm:gap-3">
            {/* Quick Storefront Button */}
            <Link
              to="/"
              className="hidden md:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold text-foreground/80 hover:text-[#CF2F2F] hover:bg-muted/50 border border-border/60 transition-colors"
              title="Browse Marketplace Storefront"
            >
              <Store className="size-3.5 text-[#CF2F2F]" />
              <span>Storefront</span>
            </Link>

            {/* Shopping Cart Trigger */}
            <button
              type="button"
              onClick={() => setIsCartOpen(true)}
              className="relative p-2 text-foreground/80 transition-colors hover:text-[#CF2F2F] rounded-lg hover:bg-muted/50"
              title="Shopping Cart"
            >
              <ShoppingBag className="size-5" />
              {cartItemCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 flex size-4 items-center justify-center rounded-full bg-[#CF2F2F] px-1 text-[10px] font-bold text-white shadow-xs">
                  {cartItemCount}
                </span>
              )}
            </button>

            {/* Theme Toggle */}
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={toggleTheme}
              aria-label="Toggle theme"
              title={`Switch to ${theme === 'dark' ? 'Light' : 'Dark'} Mode`}
            >
              {theme === 'dark' ? (
                <Sun aria-hidden className="size-5 text-amber-400" />
              ) : (
                <Moon aria-hidden className="size-5 text-foreground" />
              )}
            </Button>

            {/* Notifications */}
            <Button type="button" variant="ghost" size="icon" aria-label="Notifications">
              <Bell aria-hidden className="size-5" />
            </Button>

            {/* User Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger>
                <Avatar fallback={initials || 'DA'} alt={`${user?.firstName ?? 'Account'} avatar`} />
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                <DropdownMenuLabel>
                  {user ? `${user.firstName} ${user.lastName}` : 'My Account'}
                  <div className="text-[10px] text-muted-foreground font-normal">{user?.email}</div>
                </DropdownMenuLabel>
                <DropdownMenuItem icon={<LayoutDashboard />} onSelect={() => navigate(dashboardHref)}>
                  Dashboard
                </DropdownMenuItem>
                <DropdownMenuItem icon={<PackageOpen />} onSelect={() => navigate(appRoutes.customer.orders)}>
                  My Orders
                </DropdownMenuItem>
                <DropdownMenuItem icon={<Store />} onSelect={() => navigate('/')}>
                  Browse Storefront
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem icon={<LogOut />} onSelect={handleLogout}>
                  Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        <main className="min-h-[calc(100vh-4rem)]">
          <PageContainer size="full" className="py-6 sm:py-8 px-4 sm:px-6 lg:px-8">
            <Outlet />
          </PageContainer>
        </main>
      </div>

      {/* Global Shopping Cart Drawer */}
      <CartDrawer isOpen={isCartOpen} onClose={() => setIsCartOpen(false)} />
    </div>
  )
}